import React, { createContext, useContext, useState, useEffect } from "react";

// Translation data
const translations = {
  vi: {
    nav: {
      about_us: "Về chúng tôi",
      publications: "Công bố khoa học",
      open_source: "Mã nguồn mở",
      Webinars: "Hội thảo trực tuyến",
      research: "Nghiên cứu",
      members: "Thành viên",
      partners: "Đối tác",
      donations: "Tài trợ",
      contact: "Liên hệ",
    },
    donations: {
      title: "Tài trợ",
    },

    footer: {
      description: "VietQubit | Mang đến sự sáng tạo",
      about: "Về chúng tôi",
      adminLogin: "Quản trị",
      terms: "Điều khoản sử dụng",
      privacy: "Chính sách bảo mật",
      brand: "Hướng dẫn sử dụng thương hiệu",
    },
    terms: {
      mainTitle: "Điều Khoản Sử Dụng",
      lastUpdate: "Cập nhật lần cuối: 31/12/2025",
      conclusionTitle: "Xác nhận của người dùng",
      conclusion: "Bằng việc sử dụng trang web VietQubit, bạn xác nhận rằng:",
      conclusion1: "Bạn đã đọc và hiểu toàn bộ Điều khoản sử dụng này",
      conclusion2: "Bạn đồng ý tuân thủ tất cả các điều khoản và điều kiện",
      conclusion3: "Bạn có đủ năng lực pháp lý để tham gia vào thỏa thuận này",
      conclusion4: "Bạn sẽ sử dụng Website một cách có trách nhiệm và tuân thủ pháp luật",
      validity: "Điều khoản sử dụng này có hiệu lực kể từ ngày được công bố và sẽ tiếp tục có hiệu lực cho đến khi được thay thế bởi phiên bản mới.",
      brandtagline: "VietQubit - Mang đến sự sáng tạo",
      version: "Phiên bản: 1.0",
      originalLang: "Ngôn ngữ gốc: Tiếng Việt",
      otherLang: "Các phiên bản khác: English",
    },
    langNotificationPrefix: "Ngôn ngữ được hiển thị bằng",
  },

  en: {
    nav: {
      about_us: "About us",
      publications: "Publications",
      open_source: "Open-source",
      research: "Research",
      Webinars: "Webinars",
      members: "Members",
      partners: "Partners",
      donations: "Donations",
      contact: "Contact",
    },
    donations: {
      title: "Donations",
    },

    footer: {
      description: "VietQubit | Bring the creativity",
      about: "About us",
      adminLogin: "Admin",
      terms: "Terms of use",
      privacy: "Privacy policy",
      brand: "Brand guidelines",
    },
    terms: {
      mainTitle: "Terms of Use",
      lastUpdate: "Last updated: December 31, 2025",
      conclusionTitle: "User acknowledgment",
      conclusion: "By using the VietQubit website, you acknowledge that:",
      conclusion1: "You have read and understood these Terms of Use in their entirety",
      conclusion2: "You agree to comply with all terms and conditions",
      conclusion3: "You have the legal capacity to enter into this agreement",
      conclusion4: "You will use the Website responsibly and in compliance with the law",
      validity: "These terms of use are effective from the date of publication and will remain in effect until replaced by a new version.",
      brandtagline: "VietQubit - Bring the creativity",
      version: "Version: 1.0",
      originalLang: "Original language: Vietnamese",
      otherLang: "Other versions: English",
    },
    langNotificationPrefix: "The language has been displayed in",
  },
};

// Translation Context
const TranslationContext = createContext();

// Translation Provider Component
export function TranslationProvider({ children }) {
  const [currentLanguage, setCurrentLanguage] = useState("vi");
  const supportedLanguages = ["vi", "en"];

  // Initialize language on mount
  useEffect(() => {
    detectLanguage();
  }, []);

  // Update document title when language changes
  useEffect(() => {
    const titleTranslations = {
      vi: "VietQubit | Mang đến sự sáng tạo",
      en: "VietQubit | Bring the creativity",
    };

    // Check if we're on terms page
    if (window.location.pathname.includes("terms")) {
      const termsTitle = {
        vi: "Điều khoản sử dụng | VietQubit",
        en: "Terms of use | VietQubit",
      };
      document.title = termsTitle[currentLanguage] || termsTitle["vi"];
    } else {
      document.title = titleTranslations[currentLanguage] || titleTranslations["vi"];
    }
  }, [currentLanguage]);

  // Detect language from localStorage or browser
  const detectLanguage = async () => {
    const savedLang = localStorage.getItem("preferred-language");
    if (savedLang && supportedLanguages.includes(savedLang)) {
      setCurrentLanguage(savedLang);
      return;
    }

    const geoLanguage = await detectLanguageByCountry();
    if (geoLanguage) {
      setCurrentLanguage(geoLanguage);
      localStorage.setItem("preferred-language", geoLanguage);
      return;
    }

    const browserLang = navigator.language || navigator.userLanguage;
    const langCode = browserLang.split("-")[0].toLowerCase();

    const languageMap = {
      vi: "vi",
      en: "en",
    };

    const detectedLang = languageMap[langCode] || "en";
    setCurrentLanguage(detectedLang);
    localStorage.setItem("preferred-language", detectedLang);
  };

  const detectLanguageByCountry = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 1500);

      const response = await fetch("https://ipapi.co/country/", {
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        return null;
      }

      const countryCode = (await response.text()).trim().toUpperCase();
      if (!countryCode) {
        return null;
      }

      return countryCode === "VN" ? "vi" : "en";
    } catch {
      return null;
    }
  };

  // Change language
  const changeLanguage = (newLang) => {
    if (!supportedLanguages.includes(newLang)) {
      console.warn(`Language ${newLang} is not supported`);
      return;
    }

    const previousLanguage = currentLanguage;
    setCurrentLanguage(newLang);
    localStorage.setItem("preferred-language", newLang);

    // Dispatch language change event for compatibility
    const event = new CustomEvent("languageChanged", {
      detail: {
        language: newLang,
        previousLanguage: previousLanguage,
      },
    });
    document.dispatchEvent(event);

    // Show notification
    showLanguageChangeNotification(newLang);

    // Update document language
    document.documentElement.setAttribute("lang", newLang);
    document.body.classList.remove("lang-vi", "lang-en");
    document.body.classList.add(`lang-${newLang}`);
  };

  // Get nested translation value
  const getTranslation = (key) => {
    const langData = translations[currentLanguage];
    if (!langData) return null;

    return key.split(".").reduce((current, k) => {
      return current && current[k] !== undefined ? current[k] : null;
    }, langData);
  };

  // Format number according to language
  const formatNumber = (number) => {
    const locales = {
      vi: "vi-VN",
      en: "en-US",
    };

    return new Intl.NumberFormat(locales[currentLanguage] || "vi-VN").format(number);
  };

  // Format date according to language
  const formatDate = (date) => {
    const locales = {
      vi: "vi-VN",
      en: "en-US",
    };

    return new Intl.DateTimeFormat(locales[currentLanguage] || "vi-VN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(date);
  };

  const value = {
    currentLanguage,
    supportedLanguages,
    translations: translations[currentLanguage],
    changeLanguage,
    getTranslation,
    formatNumber,
    formatDate,
    t: getTranslation, // Alias for convenience
  };

  return <TranslationContext.Provider value={value}>{children}</TranslationContext.Provider>;
}

// Custom hook to use translations
export function useTranslation() {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error("useTranslation must be used within a TranslationProvider");
  }
  return context;
}

// Language change notification
function showLanguageChangeNotification(newLang) {
  const langNames = {
    vi: "Tiếng Việt",
    en: "English",
  };

  const currentLang = newLang || "vi";

  const prefix =
    translations[currentLang] && translations[currentLang].langNotificationPrefix
      ? translations[currentLang].langNotificationPrefix
      : "Language changed to";

  const notification = document.createElement("div");
  notification.className = "lang-notification";
  notification.textContent = `${prefix} ${langNames[newLang] || langNames["vi"]}`;

  document.body.appendChild(notification);

  setTimeout(() => {
    notification.style.opacity = "1";
    notification.style.transform = "translateY(0)";
  }, 100);

  setTimeout(() => {
    notification.style.opacity = "0";
    notification.style.transform = "translateY(-20px)";
    setTimeout(() => {
      if (document.body.contains(notification)) {
        document.body.removeChild(notification);
      }
    }, 300);
  }, 2000);
}

// Make translations globally available for compatibility
window.translations = translations;
